import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { EASE } from "./motion-primitives";

// Reusable editorial destination card (Step 5).
export const DestinationCard = ({ d, onOpen }) => (
  <motion.button
    type="button"
    onClick={() => onOpen(d)}
    data-testid={`destination-card-${d.id}`}
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-8% 0px" }}
    transition={{ duration: 0.7, ease: EASE }}
    className="group flex flex-col text-left"
  >
    <div className="relative aspect-[4/5] w-full overflow-hidden">
      <img
        src={d.image}
        alt={`${d.destination}, ${d.country}`}
        loading="lazy"
        className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-ink/10 to-transparent" />
      <span className="absolute left-4 top-4 font-sans text-[0.6rem] uppercase tracking-[0.26em] text-cream/80">
        {d.suggestedMood}
      </span>
      <span className="absolute right-4 top-4 text-cream/70 opacity-0 transition-opacity duration-500 group-hover:opacity-100">
        <ArrowUpRight size={18} strokeWidth={1.5} />
      </span>
    </div>
    <div className="pt-5">
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="font-serif text-2xl font-light tracking-tight text-cream">
          {d.destination}
        </h3>
        <span className="font-sans text-xs uppercase tracking-[0.2em] text-sand">{d.country}</span>
      </div>
      <p className="mt-3 font-serif text-base font-light italic leading-snug text-cream/80">
        “{d.pov}”
      </p>
      <div className="mt-4 flex items-center gap-2 font-sans text-[0.7rem] uppercase tracking-[0.16em] text-sand">
        <span className="text-teal">Best for</span>
        <span>{d.bestFor.join(" · ")}</span>
      </div>
    </div>
  </motion.button>
);

// Mood card (Step 2) — editorial, image-led.
export const MoodCard = ({ mood, active, onClick, large }) => (
  <button
    type="button"
    onClick={() => onClick(mood.id)}
    data-testid={`mood-card-${mood.id}`}
    className={`group relative flex overflow-hidden text-left transition-all duration-500 ${
      large ? "min-h-[320px]" : "min-h-[260px]"
    } ${active ? "ring-1 ring-teal" : ""}`}
  >
    <img
      src={mood.image}
      alt={mood.label}
      loading="lazy"
      className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
    />
    <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/50 to-ink/10 transition-colors duration-500 group-hover:from-ink/90" />
    <div className="relative z-10 mt-auto p-6">
      <div className="flex items-center gap-3">
        <span
          className={`h-px w-6 transition-all duration-500 ${active ? "bg-teal" : "bg-cream/40 group-hover:bg-teal"}`}
        />
        <h3 className="font-serif text-3xl font-light tracking-tight text-cream">{mood.label}</h3>
      </div>
      <p className="mt-3 max-w-xs font-sans text-sm leading-relaxed text-cream/75">{mood.blurb}</p>
    </div>
  </button>
);

// Wide editorial concept card (Step 4) — used in a horizontal scroller.
export const CollectionCard = ({ c }) => (
  <div
    data-testid={`collection-card-${c.id}`}
    className="group relative flex h-[440px] w-[80vw] shrink-0 snap-start overflow-hidden sm:w-[520px] lg:w-[560px]"
  >
    <img
      src={c.image}
      alt={c.title}
      loading="lazy"
      className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
    />
    <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-transparent" />
    <div className="relative z-10 mt-auto p-8">
      <span className="font-sans text-[0.65rem] uppercase tracking-[0.26em] text-teal">{c.tag}</span>
      <h3 className="mt-4 max-w-md font-serif text-3xl font-light leading-[1.08] tracking-tight text-cream sm:text-4xl">
        {c.title}
      </h3>
      <p className="mt-4 max-w-sm font-sans text-sm leading-relaxed text-cream/75">{c.blurb}</p>
    </div>
  </div>
);

// Travel-style chip (Step 3).
export const StyleChip = ({ style, active, onClick }) => (
  <button
    type="button"
    onClick={() => onClick(style.id)}
    data-testid={`style-chip-${style.id}`}
    className={`rounded-full border px-6 py-3 font-sans text-sm transition-colors duration-300 ${
      active
        ? "border-teal bg-teal text-ink"
        : "border-charcoal text-cream/85 hover:border-teal/60 hover:text-cream"
    }`}
  >
    {style.label}
  </button>
);
